import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useGetTaskTemplateCaseList, useGetTaskTemplateCaseListAttackType } from './utils'
import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
  Upload,
} from 'antd'
import ComponentTitle from '@/pages/safety-equipment/widgets/component-title'
import { debounce } from 'lodash'
import { formatDateTime } from '@/utils/date'
import { TablePaginationConfig } from 'antd/es/table'
import { FilterValue, SorterResult } from 'antd/es/table/interface'
import { ColumnProps } from 'antd/lib/table'
import lowImg from '@/assets/imges/leak-low.png'
import centerkImg from '@/assets/imges/leak-center.png'
import highImg from '@/assets/imges/leak-high.png'
import severityImg from '@/assets/imges/leak-severity.png'
import { IconFont, Message, SvgIcon } from '@/components'
import { TemplateCaseListProps } from '@/api/endpoint-security/list'
import { useCaseDetail } from '../create-task/utils'
import { useVerificationModeList } from '@/pages/useCase-knowledge-base/template-center/util'
import DownloadModal from '../create-task/download-modal'
import { t } from '@/i18n'

const { Text, Paragraph, Link } = Typography
const { useForm } = Form

interface PropsType {
  open: boolean
  templateId: string
  taskId: string
  onClose: () => void
}

interface UseCaseListProps {
  resourceId: string
  name: string
  riskLevel: string
  attackTypeName: number
  executionSceneId: string
  updateTime: string
  resourceLevel: string
}

const UsCaseModal: React.FC<PropsType> = ({ open, templateId, taskId, onClose }) => {
  const [form] = useForm()
  const [searchValue, setSearchValue] = useState('')
  const [total, setTotal] = useState(0)
  const [useCaseList, setUseCaseList] = useState<UseCaseListProps[]>([])
  const [expandedRowKey, setExpandedRowKey] = useState<string>('')
  const [caseDetail, setCaseDetail] = useState<any>({})
  const [searchParams, setSearchParams] = useState<TemplateCaseListProps>({
    page: 1,
    pageSize: 10,
  })
  const [resourceSelectOpen, setResourceSelectOpen] = useState(false)
  const [riskSelectOpen, setRiskSelectOpen] = useState(false)
  const [attackTypeSelectOpen, setAttackTypeSelectOpen] = useState(false)
  const [downloadModalVisible, setDownloadModalVisible] = useState<{
    visible: boolean
    resourceId: string
    sha256: string
  }>({
    visible: false,
    resourceId: '',
    sha256: '',
  })

  // 获取主任务关联模板的用例列表的攻击类型
  const [getAttackType, attackTypeList] = useGetTaskTemplateCaseListAttackType()
  // 获取主任务关联模板的用例列表
  const [getTaskTemplateCaseList, getTaskTemplateCaseListLoading] = useGetTaskTemplateCaseList()
  // 获取用例详情
  const [getUseCaseDetail, getUseCaseDetailLoading] = useCaseDetail()
  // 获取验证模式列表
  const [getVerificationModeList, verificationModeList] = useVerificationModeList()

  const columns: ColumnProps<UseCaseListProps>[] = [
    {
      title: t('用例名称'),
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: name => (
        <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 160 }}>
          {name}
        </Text>
      ),
    },
    {
      title: t('用例分级'),
      dataIndex: 'resourceLevel',
      key: 'resourceLevel',
      width: 85,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={resourceSelectOpen}
            onDropdownVisibleChange={setResourceSelectOpen}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setResourceSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              {
                value: 'ResourceLevelSenior',
                label: t('高级'),
              },
              {
                value: 'ResourceLevelMiddle',
                label: t('中级'),
              },
              {
                value: 'ResourceLevelJunior',
                label: t('初级'),
              },
            ]}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      filteredValue: searchParams?.resourceLevel || null,
      render: level => {
        const levelMap = {
          ResourceLevelSenior: t('高级'),
          ResourceLevelMiddle: t('中级'),
          ResourceLevelJunior: t('初级'),
        }
        const config = {
          高级: { color: '#fff', bg: '#F44F4F' },
          中级: { color: '#fff', bg: '#FFA709' },
          初级: { color: '#fff', bg: '#6F9FFF' },
        }
        const displayLevel = levelMap[level] || t('初级')
        const style = config[displayLevel]
        return (
          <TypeTag bgColor={style.bg} color={style.color}>
            {displayLevel}
          </TypeTag>
        )
      },
    },
    {
      title: t('危险等级'),
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 85,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={riskSelectOpen}
            onDropdownVisibleChange={setRiskSelectOpen}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setRiskSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              {
                value: 'risk_level_critical',
                label: t('严重'),
              },
              {
                value: 'risk_level_high',
                label: t('高危'),
              },
              {
                value: 'risk_level_medium',
                label: t('中危'),
              },
              {
                value: 'risk_level_low',
                label: t('低危'),
              },
            ]}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      filteredValue: searchParams?.riskLevel || null,
      render: level => {
        const levelMap = {
          risk_level_critical: t('严重'),
          risk_level_high: t('高危'),
          risk_level_medium: t('中危'),
          risk_level_low: t('低危'),
        }
        const config = {
          严重: { color: '#B70000', bg: '#F5D9D9' },
          高危: { color: '#FF4141', bg: '#FFE1E1' },
          中危: { color: '#FFA709', bg: '#FFEDCE' },
          低危: { color: '#6F9FFF', bg: '#E1EBFE' },
        }
        const displayLevel = levelMap[level] || t('低危')
        const style = config[displayLevel]
        return (
          <span>
            <img
              src={
                level === 'risk_level_low'
                  ? lowImg
                  : level === 'risk_level_medium'
                  ? centerkImg
                  : level === 'risk_level_high'
                  ? highImg
                  : severityImg
              }
              alt=""
              style={{ width: '12px', height: '12px', marginRight: '4px' }}
            />

            <TypeTag bgColor={style.bg} color={style.color}>
              {displayLevel}
            </TypeTag>
          </span>
        )
      },
    },
    {
      title: t('攻击类型'),
      dataIndex: 'attackTypeName',
      key: 'attackTypeName',
      width: 100,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={attackTypeSelectOpen}
            onDropdownVisibleChange={setAttackTypeSelectOpen}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setAttackTypeSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={attackTypeList.map(item => ({
              value: item.attackTypeId,
              label: item.attackTypeName,
            }))}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      filteredValue: searchParams?.attackTypeId,
      render: attackTypeName => (
        <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 100 }}>
          {attackTypeName || '-'}
        </Text>
      ),
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (text: string) => (
        <Tooltip title={formatDateTime(text, 'standard')}>
          {formatDateTime(text, 'date') || '-'}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Button type="link" onClick={() => handleExpand(record)} style={{ padding: '0' }}>
          {expandedRowKey === record.resourceId ? t('收起') : t('展开')}
        </Button>
      ),
    },
  ]

  // 使用useCallback包装防抖的搜索函数
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchParams(prev => ({
        ...prev,
        page: 1,
        resourceName: value,
      }))
    }, 300),
    [], // 空依赖数组，因为这个函数不需要依赖任何变化的值
  )

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    console.log(filters)
    const newParams = { ...searchParams }

    // 处理分页
    if (pagination.current) {
      newParams.page = pagination.current
    }
    if (pagination.pageSize) {
      newParams.pageSize = pagination.pageSize
    }

    // 处理筛选
    if (filters.resourceLevel?.length) {
      newParams.resourceLevel = filters.resourceLevel as string[]
    } else {
      delete newParams.resourceLevel
    }
    if (filters.riskLevel?.length) {
      newParams.riskLevel = filters.riskLevel as string[]
    } else {
      delete newParams.riskLevel
    }
    if (filters.attackTypeName?.length) {
      const attackTypeIds = filters.attackTypeName as string[]
      newParams.attackTypeId = attackTypeIds
    } else {
      delete newParams.attackTypeId
    }

    // 处理排序
    if (!Array.isArray(sorter) && sorter.field && sorter.order) {
      newParams.sortField = 'sort_field_update_time'
      newParams.sortOrder =
        sorter.order === 'ascend' ? 'SORT_ORDER_ASCENDING' : 'SORT_ORDER_DESCENDING'
    }

    setSearchParams(newParams)
  }

  const handleExpand = async (record: UseCaseListProps) => {
    if (!record.resourceId) return

    const isExpanded = expandedRowKey === record.resourceId

    if (isExpanded) {
      setExpandedRowKey('')
      setCaseDetail(undefined)
    } else {
      try {
        const res = await getUseCaseDetail(record.resourceId)
        if (!res || !res.data) return
        res.data.param.forEach((item, index) => {
          if (item.isShow === 1) {
            form.setFieldsValue({ [`${index}value`]: item.value })
          }
        })

        setCaseDetail(res.data)
      } catch (error: any) {
        // Message.error('获取用例详情失败')
      }
      setExpandedRowKey(record.resourceId)
    }
  }

  // 添加展开行渲染
  const expandedRowRender = () => {
    if (!caseDetail) {
      return (
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          {getUseCaseDetailLoading ? t('加载中...') : t('暂无数据')}
        </div>
      )
    }

    return (
      <ExpandedContent>
        <div className="left-section">
          <div className="info-row">
            <div className="info-item">
              <span className="label">{t('验证模式：')}</span>
              <span className="value">
                {
                  verificationModeList
                    .find(item => item.id === String(caseDetail.verificationModeId))
                    ?.name.split('->')[0]
                }
                <SvgIcon
                  name="箭头_向右"
                  width={14}
                  height={14}
                  style={{ verticalAlign: 'middle', margin: '0 4px' }}
                />

                {
                  verificationModeList
                    .find(item => item.id === String(caseDetail.verificationModeId))
                    ?.name.split('->')[1]
                }
              </span>
            </div>
            <div className="info-item">
              <span className="label">{t('操作系统：')}</span>
              <span className="value">
                {caseDetail.supportedPlatforms?.includes('supported_platform_windows') && (
                  <span style={{ marginRight: 8 }}>Windows</span>
                )}
                {caseDetail.supportedPlatforms?.includes('supported_platform_linux') && (
                  <span style={{ marginRight: 8 }}>Linux</span>
                )}
              </span>
            </div>
          </div>
          <div className="info-row">
            <div className="info-item">
              <span className="label">{t('平台架构：')}</span>
              <span className="value">
                {caseDetail.supportedArchitectures?.map((v, i) => (
                  <span key={i} style={{ marginRight: 8 }}>
                    {v == 1 ? 'X86' : 'ARM'}
                  </span>
                ))}
              </span>
            </div>
            <div className="info-item">
              <span className="label">{t('攻击类型')}</span>
              <span className="value">{caseDetail.attackType || '-'}</span>
            </div>
          </div>
          <div className="info-row">
            {caseDetail.param?.length > 0 && (
              <div className="info-item vertical">
                <div className="label" style={{ width: '110px' }}>
                  {t('用例参数配置：')}
                </div>
                <div className="value">
                  <Form form={form}>
                    <div className="params-deploy">
                      {caseDetail.param?.map(
                        (item, index) =>
                          item.isShow === 1 && (
                            <ParamItemComponent
                              key={index}
                              item={item}
                              index={index}
                              form={form}
                              onParamChange={() => {}}
                            />
                          ),
                      )}
                    </div>
                  </Form>
                </div>
              </div>
            )}
            {caseDetail.sample?.length > 0 && (
              <div className="info-item vertical">
                <div className="label">{t('样本文件：')}</div>
                <div className="value">
                  {caseDetail.sample?.map((item, index) => (
                    <div
                      key={item.name}
                      className="sample"
                      style={
                        index === caseDetail.sample.length - 1
                          ? { borderBottom: 'none', paddingBottom: 0 }
                          : {}
                      }
                    >
                      <div className="sample-header">
                        <div>
                          <IconFont type="icon-047-file" style={{ marginLeft: 0 }} />
                          <span>{item.name}</span>
                        </div>
                        <div
                          className="download-btn"
                          onClick={() =>
                            setDownloadModalVisible({
                              visible: true,
                              resourceId: caseDetail.resourceId,
                              sha256: item.sha256,
                            })
                          }
                        >
                          <IconFont type="icon-xiazai2" />
                          <span>{t('下载')}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: 12, display: 'flex', marginTop: 10 }}>
                        <div className="title">SHA-256：</div>
                        <Text
                          ellipsis={{
                            tooltip: item.sha256,
                          }}
                          style={{
                            width: 'calc(100% - 70px)',
                            wordWrap: 'break-word',
                            wordBreak: 'break-all',
                          }}
                        >
                          {item.sha256}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="vertical-items">
            {caseDetail.tactics?.length > 0 && (
              <div className="vertical-item">
                <div className="label">ATT&CK：</div>
                <div className="value-wrapper">
                  {caseDetail.tactics?.map(item => (
                    <Link key={item.name} href={item.url} target="_blank" className="link">
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <div className="vertical-items">
              <div className="vertical-item">
                <div className="label">{t('用例标签：')}</div>
                <div className="value">
                  {caseDetail.resourceTagList?.map(item => (
                    <div key={item.tagCategoryName} className="value-wrapper">
                      <div className="group">
                        <div className="group-name">{item.tagCategoryName}:</div>
                        <div className="group-tags">
                          {item.tagList?.map(tag => (
                            <div key={tag.tagName} className="tag">
                              {tag.tagName}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="vertical-item">
              <div className="label">{t('描述：')}</div>
              <div className="value-wrapper">
                <div className="value description">{caseDetail.describe || t('描述')}</div>
              </div>
            </div>
            <div className="vertical-item">
              <div className="label">{t('判断原理：')}</div>
              <div className="value-wrapper">
                <div className="value description">{caseDetail.principle || t('判断原理')}</div>
              </div>
            </div>
            <div className="vertical-item">
              <div className="label">{t('解决方案：')}</div>
              <div className="value-wrapper">
                <div className="value solution">{caseDetail.solution || t('解决方案')}</div>
              </div>
            </div>
          </div>
        </div>
      </ExpandedContent>
    )
  }

  const handleSearch = (value: string) => {
    setSearchValue(value)
    debouncedSearch(value)
  }

  const fetchUseCaseList = async () => {
    try {
      const res = await getTaskTemplateCaseList(taskId, templateId, searchParams)
      if (!res || !res.data) return
      setUseCaseList(res.data.list)
      setTotal(res.data.total)
    } catch (error) {}
  }

  useEffect(() => {
    if (open && templateId && taskId) {
      fetchUseCaseList()
    }
  }, [searchParams, open])

  useEffect(() => {
    if (open && templateId && taskId) {
      getAttackType(taskId, templateId).catch(_ => {})
    }

    getVerificationModeList().catch(_ => {})

    return () => {
      setUseCaseList([])
      setTotal(0)
      setSearchValue('')
      setExpandedRowKey('')
      setCaseDetail(undefined)
      setResourceSelectOpen(false)
      setRiskSelectOpen(false)
      setAttackTypeSelectOpen(false)
      setSearchParams({
        page: 1,
        pageSize: 10,
      })
    }
  }, [open])
  return (
    <Wrapper
      width={1050}
      title={<ComponentTitle title={t('用例列表')} />}
      open={open}
      onCancel={onClose}
      centered
      footer={null}
      zIndex={1001}
      destroyOnClose
    >
      <div className="search-bar">
        <Input
          placeholder={t('用例名称')}
          prefix={<IconFont type="icon-sousuo2" style={{ fontSize: '20px', color: '#666' }} />}
          style={{ width: 260 }}
          value={searchValue}
          onChange={e => handleSearch(e.target.value)}
          onPressEnter={e => handleSearch(e.currentTarget.value)}
        />
      </div>
      <Table
        dataSource={useCaseList}
        columns={columns}
        loading={getTaskTemplateCaseListLoading}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: expandedRowRender,
          expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
          showExpandColumn: true,
          columnWidth: 0,
          expandIconColumnIndex: -1,
        }}
        rowKey="resourceId"
        className="expanded-table"
        pagination={{
          total,
          showTotal: total => (
            <span>
              {t('共')}
              <span style={{ color: '#4238c9' }}>{total}</span>
              {t('条')}
            </span>
          ),

          pageSize: searchParams.pageSize,
          current: searchParams.page,
          pageSizeOptions: ['10', '20', '30', '40', '50'],
          showSizeChanger: true,
          showQuickJumper: true,
        }}
      />

      <DownloadModal
        visible={downloadModalVisible.visible}
        onClose={() => setDownloadModalVisible({ visible: false, resourceId: '', sha256: '' })}
        resourceId={downloadModalVisible.resourceId}
        sha256={downloadModalVisible.sha256}
      />
    </Wrapper>
  )
}

export default UsCaseModal

const Wrapper = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    position: relative;

    .ant-modal-close {
      width: 36px;
      height: 36px;
      background: #d8d6f4;
      box-shadow: -1px 4px 10px 0px #c7c7c7;
      border-radius: 6px;
      border: none;
      color: #4238c9;
      font-size: 16px;
      font-weight: 500;
      position: absolute;
      right: -8px;
      top: -8px;
      transition: transform 0.3s ease;

      &:hover {
        transform: translate(-18px, 18px);
        box-shadow: 0 0 0 0;
      }

      .ant-modal-close-x {
        width: 100%;
        height: 100%;
        line-height: 36px;
        text-align: center;
      }
    }

    .ant-modal-header {
      height: 56px;
      background: #f2f0ff;
      border-bottom: none;
      margin: 0;
      padding: 19px 20px;
    }

    .ant-modal-body {
      padding: 16px;
      min-height: 500px;

      .search-bar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 8px;

        .ant-select {
          width: 105px;
          margin-right: -1px;

          .ant-select-selector {
            border-radius: 6px 0 0 6px;
          }
        }

        .ant-input {
          width: 250px;
          margin-right: 10px;
          border-radius: 0 6px 6px 0;
        }
      }

      .ant-space {
        gap: 0 !important;

        &.space-btn .ant-space-item {
          margin-right: 8px !important;
        }
      }

      .ant-table-column-sorter {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .ant-table-column-sorters {
        justify-content: flex-start;

        .ant-table-column-title {
          flex: none;
        }
      }

      .ant-table-filter-column {
        justify-content: flex-start;

        .ant-table-column-title {
          flex: unset;
        }

        .ant-table-filter-trigger:hover {
          background: transparent;
        }
      }
    }
  }
`

const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`

const TypeTag = styled.span<{ bgColor: string; color: string }>`
  display: inline-block;
  height: 18px;
  padding: 0 4px;
  background: ${props => props.bgColor};
  border-radius: 4px;
  line-height: 18px;
  color: ${props => props.color};
  font-size: 12px;
  margin-right: 4px;
`

const ExpandedContent = styled.div`
  padding: 0 10px;
  color: #4e5969;
  font-size: 13px;
  max-height: 300px;
  overflow-y: auto;
  background-color: #fff;

  .left-section {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      height: 235px;
      width: 1px;
      border-left: 1px dashed #e5e6eb;
    }

    .info-row {
      display: flex;
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }

      .info-item {
        display: flex;
        width: 50%;

        &:nth-child(2) {
          padding-left: 15px;
        }

        &.full-width {
          width: 100%;
        }

        .label {
          width: 70px;
          color: #86909c;
          flex-shrink: 0;
        }

        .value {
          flex: 1;
        }
      }

      .vertical {
        flex-direction: column;

        .value {
          background-color: #f8f9fb;
          padding: 16px 12px;
          padding-bottom: 0;
          margin-right: 14px;
          border-radius: 6px;
          display: flex;
          flex-direction: column;

          .params-deploy {
            display: flex;
            flex-direction: column;

            .params-flex {
              display: flex;
              justify-content: space-between;
              gap: 8px;

              .params-title {
                line-height: 32px;
              }

              .describe {
                width: 340px;
                font-size: 12px;
                color: #999999;
              }

              .expand-btn {
                color: #4238c9;
                cursor: pointer;
                font-size: 12px;

                &:hover {
                  text-decoration: underline;
                }
              }

              .ant-form-item {
                margin-bottom: 0;
              }
            }
          }

          .sample {
            display: flex;
            flex-direction: column;
            border-bottom: 1px dashed #d5d9dd;
            padding-bottom: 10px;

            .sample-header {
              display: flex;
              justify-content: space-between;
              align-items: center;

              span {
                margin-left: 4px;
              }

              .download-btn {
                color: #4238c9;
                font-size: 12px;
                margin-right: 2px;

                &:hover {
                  cursor: pointer;
                  text-decoration: underline;
                }
              }
            }
          }
        }
      }
    }

    .vertical-items {
      margin-top: 16px;

      .vertical-item {
        margin-bottom: 16px;

        &:last-child {
          margin-bottom: 0;
        }

        .label {
          color: #86909c;
          margin-bottom: 8px;
        }

        .value-wrapper {
          background-color: #f8f9fb;
          padding: 10px;
          border-radius: 6px;

          .value {
            &.description,
            &.solution {
              white-space: pre-wrap;
              word-break: break-all;
            }
          }

          .group {
            display: flex;
            align-items: center;
            gap: 8px;

            .group-name {
              font-size: 14px;
              color: #333333;
            }

            .group-tags {
              display: flex;
              align-items: center;
              gap: 4px;

              .tag {
                font-size: 12px;
                color: #333333;
                height: 20px;
                background: #ededed;
                border-radius: 4px;
                padding: 0 4px;
              }
            }
          }

          .tags-container {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .tag-group {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
              gap: 4px;

              .group-name {
                color: #86909c;
              }

              .tag-item {
                padding: 4px;
                height: 20px;
                line-height: 12px;
                font-size: 12px;
                background: #ededed;
                border-radius: 4px;
                margin-right: 4px;
              }
            }
          }

          .link {
            display: block;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }
  }
`

const ParamItemComponent = ({ item, index, form, onParamChange }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [canExpand, setCanExpand] = useState(false)

  const commonInputProps = {
    style: { width: 355 },
    onChange: e => {
      onParamChange(index, {
        value: e.target.value,
        isPermanent: item.isPermanent === 1 ? item.isPermanent : 2,
        hasChange: true,
        type: 'text',
      })
    },
  }

  const renderInput = () => {
    switch (item.type) {
      case 'password':
        return (
          <Form.Item
            name={`${index}CommandValue`}
            rules={[
              { max: 1024, message: t('参数最多为1024字') },
              { required: true, message: t('请输入参数') },
            ]}
            validateFirst
          >
            <Input
              {...commonInputProps}
              type="password"
              onFocus={() => onParamChange(index, { value: '' })}
              onBlur={() => form.setFieldsValue({ [`${index}CommandValue`]: '●●●●●●●●' })}
              disabled
            />
          </Form.Item>
        )

      case 'text':
        return (
          <Form.Item
            name={`${index}CommandValue`}
            rules={[
              { max: 1024, message: t('参数最多为1024字') },
              { required: true, message: t('请输入参数') },
            ]}
            validateFirst
          >
            <Input {...commonInputProps} type="text" disabled />
          </Form.Item>
        )

      case 'file':
        return <FileInput item={item} index={index} form={form} onParamChange={onParamChange} />
      case 'bool':
        return <BoolInput item={item} index={index} onParamChange={onParamChange} />
      default:
        return (
          <Form.Item
            name={`${index}value`}
            rules={[
              { max: 1024, message: t('参数最多为1024字') },
              { required: true, message: t('请输入参数') },
              {
                pattern: item.type === 'int' ? /^[0-9]*$/ : RegExp(''),
                message: item.type === 'int' ? t('参数仅支持数字') : '',
              },
              {
                pattern: item.type === 'float' ? /^(\d+)(\.\d+)?$/ : RegExp(''),
                message: item.type === 'float' ? t('参数仅支持数字和小数') : '',
              },
            ]}
            validateFirst
          >
            <Input
              style={{ width: 355 }}
              onChange={e =>
                onParamChange(index, {
                  value: e.target.value,
                  isPermanent: item.isPermanent === 1 ? item.isPermanent : 2,
                })
              }
              disabled
            />
          </Form.Item>
        )
    }
  }

  return (
    <div className="params-flex">
      <Text
        className="params-title"
        ellipsis={{
          tooltip: { getPopupContainer: () => document.body },
        }}
      >
        {item.key}
      </Text>
      <div className="content">
        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>{renderInput()}</div>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Paragraph
            className="describe"
            ellipsis={!isExpanded ? { rows: 1, onEllipsis: setCanExpand } : false}
          >
            {item.description}
          </Paragraph>
          {canExpand && (
            <span onClick={() => setIsExpanded(!isExpanded)} className="expand-btn">
              {isExpanded ? t('收起') : t('展开')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const FileInput = ({ item, index, form, onParamChange }) => (
  <div>
    <Form.Item
      name={`${index}value`}
      validateFirst
      rules={[{ required: true, message: t('请上传文件') }]}
    >
      <Upload
        name="file"
        action={`${
          process.env.NODE_ENV === 'dev' ? '/api' : ''
        }/portal/verify/endpointSecurity/uploadParamFile`}
        maxCount={1}
        onChange={({ file }) => {
          if (file.status === 'done') {
            const { code, data } = file.response
            if (code === 200) {
              onParamChange(index, {
                value: data?.name,
                filePath: data?.path,
                isPermanent: item.isPermanent === 1 ? item.isPermanent : 2,
              })
            }
          } else if (file.status === 'error') {
            Message.error(t('上传失败'))
          }
        }}
        disabled
      >
        <Button type="primary">{t('上传文件')}</Button>
      </Upload>
    </Form.Item>
    {item.value && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 12,
        }}
      >
        <IconFont type="icon-fujian2" />
        <Typography.Text
          style={{ marginRight: 36, maxWidth: 240 }}
          ellipsis={{
            tooltip: { getPopupContainer: () => document.body },
          }}
        >
          {item.value}
        </Typography.Text>
        <IconFont
          type="icon-shanchu-m"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            onParamChange(index, { filePath: '', value: '' })
            form.setFieldValue(`${index}value`, null)
          }}
        />
      </div>
    )}
  </div>
)

const BoolInput = ({ item, index, onParamChange }) => (
  <Form.Item name={`${index}value`} validateFirst valuePropName="checked">
    <Switch
      disabled
      onChange={(e: boolean) => {
        onParamChange(index, {
          value: e.toString(),
          isPermanent: item.isPermanent === 1 ? item.isPermanent : 2,
        })
        Message.success(`参数已${e ? t('启用') : t('停用')}`)
      }}
    />
  </Form.Item>
)
